import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { ButtonStateEnum } from "@/enums";
import { generateValidClassNameHandler } from "@/lib/utils";

const buttonVariants = cva(
    "inline-flex items-center justify-center gap-1 whitespace-nowrap rounded text-xs font-medium transition-colors hover:opacity-90 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-30 md:text-sm",
    {
        compoundVariants: [
            // Default color variants
            {
                class: "bg-primary text-primary-foreground",
                state: ButtonStateEnum.DEFAULT,
                variant: "default",
            },
            {
                class: "border-2 border-primary bg-transparent text-primary",
                state: ButtonStateEnum.DEFAULT,
                variant: "outline",
            },
            {
                class: "!h-fit !p-0 text-primary",
                state: ButtonStateEnum.DEFAULT,
                variant: "ghost",
            },
            {
                class: "bg-transparent p-0 text-primary",
                state: ButtonStateEnum.DEFAULT,
                variant: "icon",
            },
            {
                class: "justify-between border border-gray-300 bg-gray-50 font-normal text-foreground focus:border focus:border-primary",
                state: ButtonStateEnum.DEFAULT,
                variant: "input",
            },
            {
                class: "!rounded-full bg-primary text-primary-foreground",
                state: ButtonStateEnum.DEFAULT,
                variant: "circle",
            },
            // Danger color variants
            {
                class: "bg-danger text-primary-foreground",
                state: ButtonStateEnum.DANGER,
                variant: "default",
            },
            {
                class: "border-2 border-danger bg-transparent text-danger",
                state: ButtonStateEnum.DANGER,
                variant: "outline",
            },
            {
                class: "!h-fit !p-0 text-danger",
                state: ButtonStateEnum.DANGER,
                variant: "ghost",
            },
            {
                class: "bg-transparent p-0 text-danger",
                state: ButtonStateEnum.DANGER,
                variant: "icon",
            },
            {
                class: "justify-between border border-danger bg-gray-50 font-normal text-foreground focus:border focus:border-danger",
                state: ButtonStateEnum.DANGER,
                variant: "input",
            },
            {
                class: "!rounded-full bg-danger text-danger",
                state: ButtonStateEnum.DANGER,
                variant: "circle",
            },
        ],
        defaultVariants: {
            size: "default",
            state: ButtonStateEnum.DEFAULT,
            variant: "default",
        },
        variants: {
            size: {
                default: "h-10 px-4 py-2",
                icon: "min-h-6 min-w-6 px-1",
                lg: "h-11 px-8 py-2",
                sm: "h-9 px-3 py-2",
            },
            state: {
                danger: "",
                default: "",
            },
            variant: {
                circle: "",
                default: "",
                ghost: "",
                icon: "",
                input: "",
                outline: "",
            },
        },
    },
);

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> { asChild?: boolean }

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    ({
        asChild = false,
        className,
        size,
        state,
        variant,
        ...props
    }, ref) => {
        const Comp = asChild ? Slot : "button";

        return (
            <Comp
                ref={ref}
                className={generateValidClassNameHandler(
                    className,
                    buttonVariants({
                        size,
                        state,
                        variant,
                    }),
                )}
                {...props}
            />
        );
    },
);

Button.displayName = "Button";

export {
    Button,
    buttonVariants,
};
